import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plus, Filter, AlertTriangle, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(0)}`;

const RestockDialog = ({ open, product, onClose, onDone }) => {
    const [form, setForm] = useState({ qty: "10", batch_no: "", mrp: "", supplier: "", expiry_date: "", location_id: "" });
    const [bins, setBins] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && product) {
            setForm({ qty: "10", batch_no: `B-${Date.now().toString().slice(-6)}`, mrp: String(product.mrp || ""), supplier: "PILLS Wholesale", expiry_date: "", location_id: "" });
            // Lazy-load available bins (active=true). Fail silently if endpoint not available.
            api.get("/admin/inventory/locations", { params: { type: "bin" } })
                .then((r) => setBins(r.data.items || []))
                .catch(() => setBins([]));
        }
    }, [open, product]);

    if (!open || !product) return null;
    const submit = async () => {
        if (!form.batch_no.trim() || !form.expiry_date || !form.mrp || !form.qty) {
            toast.error("All fields are required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                qty: parseInt(form.qty, 10),
                batch_no: form.batch_no,
                mrp: parseFloat(form.mrp),
                supplier: form.supplier,
                expiry_date: form.expiry_date,
            };
            if (form.location_id) payload.location_id = form.location_id;
            await api.post(`/admin/products/${product.id}/restock`, payload);
            toast.success(`Restocked +${form.qty} units${form.location_id ? " · placed in bin" : ""}`);
            onDone?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Restock failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="restock-dialog">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
                <p className="label-uppercase">Inventory</p>
                <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Restock {product.name}</h2>
                <p className="mt-1 text-xs text-slate-500">Current stock: <strong>{product.stock_qty}</strong></p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Quantity</span>
                        <input type="number" min="1" value={form.qty} onChange={(e) => setForm({...form, qty: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-qty" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">MRP per unit</span>
                        <input type="number" min="0" step="0.01" value={form.mrp} onChange={(e) => setForm({...form, mrp: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-mrp" />
                    </label>
                    <label className="col-span-2 block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Batch number</span>
                        <input value={form.batch_no} onChange={(e) => setForm({...form, batch_no: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-batch" />
                    </label>
                    <label className="col-span-2 block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Supplier</span>
                        <input value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-supplier" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Expiry date</span>
                        <input type="date" value={form.expiry_date} onChange={(e) => setForm({...form, expiry_date: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-expiry" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bin <span className="text-slate-400 normal-case font-normal">(optional)</span></span>
                        <select value={form.location_id} onChange={(e) => setForm({...form, location_id: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="restock-location">
                            <option value="">— Unassigned —</option>
                            {bins.map((b) => (
                                <option key={b.id} value={b.id}>{b.code}</option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="mt-6 flex gap-2">
                    <button onClick={onClose} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
                    <button onClick={submit} disabled={busy} className="flex-1 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50" data-testid="restock-submit">
                        {busy ? "Adding..." : "Add batch"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Content = () => {
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");
    const [lowStock, setLowStock] = useState(params.get("low_stock") === "true");
    const [page, setPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [categorySlug, setCategorySlug] = useState(params.get("category") || "");
    const [restockFor, setRestockFor] = useState(null);

    useEffect(() => {
        (async () => {
            const r = await api.get("/categories");
            setCategories(r.data);
        })();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/products", {
                params: {
                    q: q.trim() || undefined,
                    category: categorySlug || undefined,
                    low_stock: lowStock || undefined,
                    page,
                    page_size: 25,
                },
            });
            setRows(r.data.items);
            setTotal(r.data.total);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, lowStock, categorySlug]);

    const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

    return (
        <div className="space-y-5" data-testid="admin-products-page">
            <div className="flex flex-wrap items-center gap-3">
                <form onSubmit={onSearch} className="relative flex-1 max-w-md">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="input-clean !rounded-lg pl-10" data-testid="products-search" />
                </form>
                <select value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)} className="input-clean !rounded-lg !w-auto" data-testid="products-category">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <input type="checkbox" checked={lowStock} onChange={(e) => setLowStock(e.target.checked)} className="accent-rose-600" data-testid="products-low-stock-toggle" />
                    <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
                    Low stock only
                </label>
                <Link to="/admin/products/new" className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600" data-testid="products-add-btn">
                    <Plus className="h-4 w-4" /> Add product
                </Link>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Product</th>
                            <th className="px-4 py-3 text-left">Category</th>
                            <th className="px-4 py-3 text-right">MRP</th>
                            <th className="px-4 py-3 text-right">Selling</th>
                            <th className="px-4 py-3 text-right">Stock</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? [0,1,2,3,4,5,6].map((i) => <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-7 skeleton rounded" /></td></tr>) :
                          rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">No products match.</td></tr> :
                          rows.map((p) => (
                            <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <img src={(p.images || [])[0] || "/pills-logo.jpg"} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-200" />
                                        <div className="min-w-0">
                                            <p className="line-clamp-1 font-medium text-slate-900">{p.name}</p>
                                            <p className="line-clamp-1 text-xs text-slate-500">{p.brand} {p.pack_size && `· ${p.pack_size}`}{p.prescription_required && <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-0.5 text-[9px] font-semibold text-brand-700"><ShieldCheck className="h-2.5 w-2.5" /> Rx</span>}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-slate-600">{p.category_slug || "—"}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-500 line-through">{inr(p.mrp)}</td>
                                <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(p.selling_price)}</td>
                                <td className="px-4 py-3 text-right">
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                                        p.stock_qty <= 0 ? "bg-rose-100 text-rose-700" :
                                        p.stock_qty <= 10 ? "bg-amber-100 text-amber-700" :
                                        "bg-emerald-50 text-emerald-700"
                                    }`}>{p.stock_qty}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                        p.active === false ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"
                                    }`}>{p.active === false ? "disabled" : "active"}</span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-1">
                                        <button onClick={() => setRestockFor(p)} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700" data-testid={`product-restock-${p.id}`}>
                                            Restock
                                        </button>
                                        <Link to={`/admin/products/${p.id}/edit`} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700" data-testid={`product-edit-${p.id}`}>
                                            Edit
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                          ))}
                    </tbody>
                </table>
                {!loading && total > 25 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Page {page} · {Math.min(page * 25, total)} of {total}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>

            <RestockDialog open={!!restockFor} product={restockFor} onClose={() => setRestockFor(null)} onDone={load} />
        </div>
    );
};

const AdminProducts = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Products" subtitle="Catalog & stock management">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminProducts;
